import React, { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Briefcase,
  User,
  Crown,
  Ticket,
  Ban,
  Info,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../context/axiosInstance";
import RescheduleModal from "./RescheduleModal";
import AppFooter from "./Homepage/AppFooter";
import ConflictResolutionToast from "./ConflictResolutionToast";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "react-hot-toast";

// --- HELPERS ---
const parseTimeStringToMinutes = (timeStr) => {
  if (!timeStr) return 0;

  // Handle "09:00 AM" or "09:00"
  const [time, modifier] = timeStr.split(" ");
  let [hours, minutes] = time.split(":").map(Number);

  if (modifier === "PM" && hours < 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;

  return hours * 60 + minutes;
};

const CalendarTimeline = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode] = useState("day");
  const [timelineItems, setTimelineItems] = useState([]);
  const [workingZones, setWorkingZones] = useState([]); // ✅ Store Zones
  const [loading, setLoading] = useState(false);

  // ✅ Updated Filters to match Mobile
  const [filters, setFilters] = useState({
    Work: true,
    Personal: true,
    Events: true,
    Blocks: true,
  });

  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [itemToReschedule, setItemToReschedule] = useState(null);
  const [showWorkDialog, setShowWorkDialog] = useState(null); // For the "Switch App" modal

  // ✅ Updated Color Scheme (Matches Mobile)
  const colorScheme = {
    work_booking: {
      bg: "rgba(59, 130, 246, 0.2)", // Blue
      border: "#3b82f6",
      text: "#60a5fa",
      icon: <Briefcase size={14} />,
    },
    personal_booking: {
      bg: "rgba(139, 92, 246, 0.2)", // Purple
      border: "#8b5cf6",
      text: "#a78bfa",
      icon: <User size={14} />,
    },
    event_hosted: {
      bg: "rgba(249, 115, 22, 0.2)", // Orange
      border: "#f97316",
      text: "#fb923c",
      icon: <Crown size={14} />,
    },
    event_invited: {
      bg: "rgba(234, 179, 8, 0.2)", // Yellow
      border: "#eab308",
      text: "#facc15",
      icon: <Ticket size={14} />,
    },
    personal_block: {
      bg: "rgba(239, 68, 68, 0.2)", // Red
      border: "#ef4444",
      text: "#f87171",
      icon: <Ban size={14} />,
    },
    default: {
      bg: "rgba(107, 114, 128, 0.2)",
      border: "#6b7280",
      text: "#9ca3af",
      icon: <Info size={14} />,
    },
  };


  // --- DATA FETCHING ---
  const fetchTimeline = useCallback(async (date) => {
    setLoading(true);
    try {
      const dateStr =
        date.getFullYear() +
        "-" +
        String(date.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(date.getDate()).padStart(2, "0");

      // Unified Endpoint
      const { data } = await AxiosInstance.get(
        `/timeline/?date=${dateStr}`,
      );

      setTimelineItems(data.timeline || []);
      setWorkingZones(data.zones || []); // ✅ Save Zones
    } catch (error) {
      console.error("Error fetching timeline:", error);
      setTimelineItems([]);
      setWorkingZones([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTimeline(selectedDate);
  }, [selectedDate, fetchTimeline]);

  // --- NAVIGATION LOGIC ---
  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (viewMode === "day") {
      newDate.setDate(newDate.getDate() + direction);
    } else {
      newDate.setDate(newDate.getDate() + direction * 7);
    }
    setSelectedDate(newDate);
  };

  // --- FILTER LOGIC ---
  const getVisibleItems = () => {
    return timelineItems.filter((item) => {
      if (item.type === "work_booking") return filters.Work;
      if (item.type === "personal_booking") return filters.Personal;
      if (item.type.startsWith("event")) return filters.Events;
      if (item.type === "personal_block") return filters.Blocks;
      return true;
    });
  };

  // --- INTERACTION LOGIC ---
  const handleItemClick = (item) => {
    if (item.is_conflicting) {
      // 1. Conflict
      toast.custom(
        (t) => (
          <ConflictResolutionToast
            t={t}
            conflictingItem={item}
            allItems={timelineItems}
            onNavigate={() => navigate("/app/appointments")}
          />
        ),
        { duration: Infinity },
      );
    } else if (item.type === "work_booking") {
      // 2. Work Item (Read Only)
      setShowWorkDialog(item);
    } else if (item.type === "personal_booking") {
      // 3. Personal Booking (Reschedule)
      setItemToReschedule(item.details);
      setShowRescheduleModal(true);
    } else if (item.type.startsWith("event")) {
      // 4. Event (Manage)
      navigate("/app/appointments");
    }
  };

  // --- LAYOUT ALGORITHM (Same as Mobile) ---
  const packOverlappingItems = (items) => {
    if (!items || items.length === 0) return [];

    // Sort by start time
    const sortedItems = [...items].sort(
      (a, b) => new Date(a.start_time) - new Date(b.start_time),
    );

    const layoutItems = [];
    let columns = [];

    sortedItems.forEach((item) => {
      const itemStart = new Date(item.start_time);
      const itemEnd = new Date(item.end_time);

      let colIndex = -1;
      for (let i = 0; i < columns.length; i++) {
        if (itemStart >= columns[i]) {
          colIndex = i;
          break;
        }
      }
      if (colIndex === -1) {
        colIndex = columns.length;
      }
      columns[colIndex] = itemEnd;

      layoutItems.push({
        ...item,
        layout: { column: colIndex, totalColumns: 1 },
      });
    });

    // Calculate widths
    for (let i = 0; i < layoutItems.length; i++) {
      const itemA = layoutItems[i];
      const startA = new Date(itemA.start_time);
      const endA = new Date(itemA.end_time);

      let maxCol = itemA.layout.column;

      for (let j = 0; j < layoutItems.length; j++) {
        if (i === j) continue;
        const itemB = layoutItems[j];
        const startB = new Date(itemB.start_time);
        const endB = new Date(itemB.end_time);

        if (startA < endB && endA > startB) {
          maxCol = Math.max(maxCol, itemB.layout.column);
        }
      }
      itemA.layout.totalColumns = maxCol + 1;
    }
    return layoutItems;
  };

  // --- RENDER GRID ---
  // --- RENDER GRID ---
  const renderTimeGrid = () => {
    const items = getVisibleItems();
    const layoutItems = packOverlappingItems(items);

    // ✅ Define constants to match Partner App
    const HOUR_HEIGHT = 80;
    const GUTTER_WIDTH = 70;

    return (
      <div className="relative" style={{ height: `${24 * HOUR_HEIGHT}px` }}>
        {" "}
        {/* ✅ 1920px total */}
        {/* 1. HATCHED BACKGROUND (Off-Hours) */}
        <div
          className="absolute inset-0 z-0 pointer-events-none opacity-50"
          style={{
             backgroundImage:
              "repeating-linear-gradient(45deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 10px)",
          }}
        />
        {/* 2. WORKING ZONES */}
        {workingZones.map((zone, idx) => {
          const startMin = parseTimeStringToMinutes(zone.start);
          const endMin = parseTimeStringToMinutes(zone.end);
          const top = startMin * (HOUR_HEIGHT / 60); // ✅ Scale by HOUR_HEIGHT
          const height = (endMin - startMin) * (HOUR_HEIGHT / 60); // ✅ Scale by HOUR_HEIGHT

          return (
            <div
              key={`zone-${idx}`}
              className="absolute border-l-[3px] border-emerald-500/50"
              style={{
                top: `${top}px`,
                height: `${height}px`,
                left: `${GUTTER_WIDTH}px`,
                width: `calc(100% - ${GUTTER_WIDTH}px)`,
                backgroundColor: "rgba(255, 255, 255, 0.02)",
                zIndex: 1,
              }}
            />
          );
        })}
        {/* 3. GRID LINES & LABELS */}
        {Array.from({ length: 25 }).map(
          (
            _,
            i, // ✅ Use 25 for cleaner iteration
          ) => (
            <div
              key={i}
              className="absolute w-full border-t border-white/5 flex items-center"
              style={{ top: `${i * HOUR_HEIGHT}px` }} // ✅ Use HOUR_HEIGHT
            >
              <div
                style={{ width: `${GUTTER_WIDTH}px` }}
                className="text-right pr-4"
              >
                {i !== 0 && (
                  <span className="text-xs text-gray-500 font-medium -mt-3 block">
                    {i === 12 ? "12 PM" : i > 12 ? `${i - 12} PM` : `${i} AM`}
                  </span>
                )}
              </div>
              {/* Dashed line inside grid */}
              <div className="flex-grow border-t border-dashed border-white/5 h-px"></div>
            </div>
          ),
        )}
        {/* 4. CURRENT TIME INDICATOR */}
        {selectedDate.toDateString() === new Date().toDateString() && (
          <div
            className="absolute w-full z-30 flex items-center pointer-events-none"
            style={{
              top: `${(new Date().getHours() * 60 + new Date().getMinutes()) * (HOUR_HEIGHT / 60)}px`, // ✅ Scale by HOUR_HEIGHT
            }}
          >
            <div
              style={{ width: `${GUTTER_WIDTH}px` }}
              className="flex justify-end pr-3"
            />
            <div className="w-2.5 h-2.5 bg-red-500 rounded-full shadow-[0_0_8px_rgba(239,68,68,0.8)] -ml-1.5 z-40" />
            <div className="h-[2px] bg-red-500 flex-grow shadow-[0_0_8px_rgba(239,68,68,0.4)]" />
          </div>
        )}
        {/* 5. TIMELINE ITEMS */}
        {layoutItems.map((item, idx) => {
          const start = new Date(item.start_time);
          const end = new Date(item.end_time);
          const startMin = start.getHours() * 60 + start.getMinutes();
          const duration = (end - start) / (1000 * 60);

          const top = startMin * (HOUR_HEIGHT / 60); // ✅ Scale by HOUR_HEIGHT
          const height = Math.max(duration * (HOUR_HEIGHT / 60), 35); // ✅ Scale by HOUR_HEIGHT

          const style = colorScheme[item.type] || colorScheme.default;

          const widthPercent = 98 / item.layout.totalColumns;
          const leftPercent = item.layout.column * widthPercent;

          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              onClick={() => handleItemClick(item)}
              className="absolute rounded-lg border-l-4 overflow-hidden cursor-pointer hover:z-50 hover:brightness-110 transition-all shadow-sm group"
              style={{
                top: `${top}px`,
                height: `${height}px`,
                left: `calc(${GUTTER_WIDTH}px + 4px + ${leftPercent}%)`,
                width: `calc(${widthPercent}% - 8px)`,
                backgroundColor: style.bg,
                borderColor: style.border,
                zIndex: 10 + item.layout.column,
              }}
            >
              <div className="p-2 h-full flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-0.5">
                  {item.is_conflicting && (
                    <AlertTriangle
                      size={14}
                      className="text-orange-500 animate-pulse"
                    />
                  )}
                  <span style={{ color: style.text }}>{style.icon}</span>
                  <p className="font-bold text-xs truncate text-white leading-none">
                    {item.title}
                  </p>
                </div>
                {height > 40 && (
                  <p className="text-[10px] text-zinc-400 truncate pl-6">
                    {item.subtitle}
                  </p>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="bg-background text-white min-h-screen font-sans selection:bg-primary-500/30 selection:text-white">
      <main className="flex-grow">
        <div className="max-w-[1600px] mx-auto px-6 py-12">
          {/* Header Section */}
          <div className="mb-8">
            <button
              onClick={() => navigate("/app")}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-widest mb-8"
            >
              <ArrowLeft size={16} /> Back to Dashboard
            </button>

            <div className="flex flex-col md:flex-row justify-between items-end gap-6">
              <h1 className="text-5xl font-black tracking-tighter">
                My Timeline
              </h1>

              <div className="flex gap-4">
                {/* Date Nav */}
                <div className="flex items-center bg-surface border border-white/10 rounded-lg p-1 backdrop-blur-sm">
                  <button
                    onClick={() => navigateDate(-1)}
                    className="p-2 hover:bg-white/10 rounded transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={() => setSelectedDate(new Date())}
                    className="px-4 text-sm font-bold"
                  >
                    Today
                  </button>
                  <button
                    onClick={() => navigateDate(1)}
                    className="p-2 hover:bg-white/10 rounded transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>

                {/* Filters */}
                <div className="flex items-center bg-surface border border-white/10 rounded-lg p-1 gap-1 backdrop-blur-sm">
                  {Object.keys(filters).map((key) => (
                    <button
                      key={key}
                      onClick={() =>
                        setFilters((prev) => ({ ...prev, [key]: !prev[key] }))
                      }
                      className={`px-3 py-2 text-xs font-bold uppercase tracking-wider rounded transition-all ${
                        filters[key]
                          ? "bg-white text-black"
                          : "text-gray-500 hover:text-gray-300"
                      }`}
                    >
                      {key}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="text-gray-400 mt-2 font-medium">
              {selectedDate.toLocaleDateString("en-US", {
                weekday: "long",
                month: "long",
                day: "numeric",
              })}
            </div>
          </div>

          {/* Timeline Container */}
          <div className="bg-surface rounded-2xl border border-white/10 overflow-hidden min-h-[80vh] relative shadow-xl backdrop-blur-sm">
            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-50">
                <Loader2 size={40} className="animate-spin text-primary-500" />
              </div>
            ) : (
              <div className="overflow-y-auto h-[800px] custom-scrollbar">
                {renderTimeGrid()}
              </div>
            )}
          </div>
        </div>
      </main>

      <div className="max-w-[1600px] mx-auto px-6 pb-8">
        <AppFooter />
      </div>
      {/* --- MODALS --- */}

      {/* 1. Reschedule Modal (Personal Bookings) */}
      <AnimatePresence>
        {showRescheduleModal && (
          <RescheduleModal
            oldBooking={itemToReschedule}
            onClose={() => setShowRescheduleModal(false)}
            onRescheduled={() => fetchTimeline(selectedDate)}
          />
        )}
      </AnimatePresence>

      {/* 2. Work Item Dialog (Read Only) */}
      <AnimatePresence>
        {showWorkDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-surface border border-white/10 p-6 rounded-2xl max-w-sm w-full shadow-2xl"
            >
              <div className="flex items-center gap-3 mb-4 text-blue-400">
                <Briefcase size={24} />
                <h3 className="text-xl font-bold text-white">Work Schedule</h3>
              </div>
              <p className="text-white font-semibold text-lg mb-1">
                {showWorkDialog.title}
              </p>
              <p className="text-gray-400 mb-6">
                Client: {showWorkDialog.subtitle}
              </p>

              <div className="bg-blue-500/10 border border-blue-500/20 p-4 rounded-xl mb-6">
                <p className="text-blue-200 text-sm">
                  This is a client booking. Please switch to the{" "}
                  <strong>Partner App</strong> to manage this appointment.
                </p>
              </div>

              <button
                onClick={() => setShowWorkDialog(null)}
                className="w-full py-3 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition-colors"
              >
                Got it
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};;

export default CalendarTimeline;
